import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FiShield, FiMail, FiLock, FiArrowRight } from 'react-icons/fi';
import AdminService from '../services/AdminService';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const history = useHistory();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Please enter email and password'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await AdminService.login(email, password);
      if (res.token) {
        const roleUpper = (res.role || '').toUpperCase();
        if (roleUpper === 'PARTNER') {
          setError('Access Denied: Partner role accounts cannot access the Admin Panel.');
          return;
        }
        const rawName = res.name;
        const nameStr = typeof rawName === 'object' ? `${rawName.first || ''} ${rawName.last || ''}`.trim() : (rawName || 'Admin');
        localStorage.setItem('admin_token', res.token);
        localStorage.setItem('admin_user', JSON.stringify({
          name: nameStr,
          email: res.email,
          image: res.image,
          _id: res._id,
          role: res.role,
          brandId: res.brandId,
          instituteId: res.instituteId,
        }));
        history.push('/app/dashboard');
      } else {
        setError('Login failed. No token received.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0e1a] relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-pink-500/8 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md px-6">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Tapcoe" className="w-16 h-16 rounded-2xl mx-auto mb-4 shadow-lg shadow-purple-500/20 object-cover" />
          <h1 className="text-2xl font-extrabold text-white tracking-tight">TAPCOE</h1>
          <p className="text-xs font-semibold text-purple-400 tracking-widest mt-1">ADMIN PANEL</p>
        </div>

        {/* Login Card */}
        <form onSubmit={handleLogin} className="glass-card rounded-2xl p-8 space-y-5">
          <div>
            <h2 className="text-lg font-bold text-white">Welcome Back</h2>
            <p className="text-xs text-slate-500 mt-1">Sign in to manage your platform</p>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-xs text-red-400 font-medium">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Email</label>
              <div className="relative">
                <FiMail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@tapcoe.com"
                  className="w-full bg-slate-900/80 border border-slate-700/60 rounded-xl pl-11 pr-4 py-3 text-sm text-white outline-none focus:border-purple-500 transition-colors placeholder:text-slate-600"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Password</label>
              <div className="relative">
                <FiLock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-slate-900/80 border border-slate-700/60 rounded-xl pl-11 pr-4 py-3 text-sm text-white outline-none focus:border-purple-500 transition-colors placeholder:text-slate-600"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-white py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:from-purple-500 hover:to-pink-400 transition-all disabled:opacity-50 shadow-lg shadow-purple-500/20"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>Sign In <FiArrowRight className="w-4 h-4" /></>
            )}
          </button>
        </form>

        <p className="text-center text-[11px] text-slate-600 mt-6">
          © {new Date().getFullYear()} Tapcoe. Platform Administration.
        </p>
      </div>
    </div>
  );
};

export default Login;
